import { type ReactNode, useState } from 'react';

import ArrowIosDown from '@crehana/compass.icons.outline/ArrowIosDown';
import ArrowIosUp from '@crehana/compass.icons.outline/ArrowIosUp';
import { alert } from '@crehana/tailwindcss/colors';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Loader } from '@crehana/ui';
import { clsx } from 'clsx';
import type { NamedProps } from 'react-select';
import { AsyncPaginate, type LoadOptions } from 'react-select-async-paginate';

import useDebounce from '../../hooks/useDebounce';
import { darkMode, lightMode } from './SearchInputTw.styles';

interface SearchInputTwProps extends Partial<Pick<NamedProps, 'styles'>> {
  closeMenuOnSelect?: boolean;
  controlShouldRenderValue?: boolean;
  controlShouldRenderValueOnClose?: boolean;
  defaultValue?: unknown;
  disable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatOptionLabel: (option: any, labelMeta: any) => ReactNode;
  hideSelectedOptions?: boolean;
  id: string;
  isClearable?: boolean;
  isDark?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  loadOptions: LoadOptions<unknown, unknown>;
  noResultsLabel?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  options?: unknown[];
  pageSize?: number;
  placeholder?: string | ReactNode;
  value: unknown;
}

/**
 * SearchInputTw only support styles for one option,
 * if you require to use multi option please work on these styles.
 */
const SearchInputTw: React.FC<SearchInputTwProps> = ({
  closeMenuOnSelect = true,
  controlShouldRenderValue = true,
  controlShouldRenderValueOnClose = false,
  defaultValue,
  disable,
  formatOptionLabel,
  hideSelectedOptions = true,
  id,
  isClearable = true,
  isDark,
  isMulti,
  isSearchable,
  loadOptions,
  noResultsLabel,
  onChange,
  options,
  pageSize,
  placeholder,
  styles,
  value,
}) => {
  const { debounceValue, handleStringChange } = useDebounce();
  const [isOpen, setIsOpen] = useState(false);
  const [hasNoResults, setHasNoResults] = useState(false);

  const NoOptionsMessage = () => {
    if (debounceValue && options?.length === 0) {
      setHasNoResults(true);
      return <></>;
    }
    setHasNoResults(false);

    return <></>;
  };

  const LoadingMessage = () => <></>;

  const LoadingIndicator = () => <Loader size={16} isWhite={isDark} />;

  const DropdownIndicator = () => {
    const Icon = isOpen ? ArrowIosUp : ArrowIosDown;
    return <Icon size={24} className="currentColor" />;
  };

  return (
    <>
      <AsyncPaginate
        additional={{ page: 1 }}
        className="w-full z-2 tw-font-body4"
        closeMenuOnSelect={closeMenuOnSelect}
        components={{
          NoOptionsMessage,
          LoadingMessage,
          LoadingIndicator,
          DropdownIndicator,
        }}
        css={
          hasNoResults
            ? `
        > div:nth-of-type(1) {
           border-color: ${alert[isDark ? 300 : 500]}};
        }`
            : ''
        }
        controlShouldRenderValue={
          controlShouldRenderValueOnClose ? !isOpen : controlShouldRenderValue
        }
        debounceTimeout={500}
        onMenuOpen={() => setIsOpen(true)}
        onMenuClose={() => setIsOpen(false)}
        defaultValue={defaultValue}
        formatOptionLabel={formatOptionLabel}
        hideSelectedOptions={hideSelectedOptions}
        instanceId={id || new Date().toString()}
        isClearable={isClearable}
        isDisabled={disable}
        isMulti={isMulti}
        isSearchable={isSearchable}
        loadOptions={loadOptions}
        onChange={onChange}
        pageSize={pageSize}
        onInputChange={value => {
          handleStringChange(value);
          setHasNoResults(false);
        }}
        placeholder={placeholder}
        // Do not remove this IGNORE, coliverav is having troubles with his IDE
        // @ts-ignore
        styles={{
          ...(isDark ? darkMode : lightMode),
          ...styles,
        }}
        value={value}
      />
      {hasNoResults && (
        <p
          className={clsx(
            isDark ? 'tw-text-alert-300' : 'tw-text-alert-500',
            'tw-font-caption  tw-mt-0.8',
          )}
        >
          {noResultsLabel}
        </p>
      )}
    </>
  );
};

export default SearchInputTw;
